/**
 * CRM (Fachmodul "crm"): gemeinsame Bausteine fuer Aktivitaeten.
 *
 * Der Erfassungsdialog wird von drei Stellen benutzt: der
 * Wiedervorlagen-Seite, der Aktivitaetenliste und der Kundenakte. Kommt er
 * mit festem Partner (Kundenakte), entfaellt die Partnerwahl und der
 * Aussendienst des Kunden ist als Zustaendiger vorbelegt.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  contactDisplayName,
  CRM_ACTIVITY_KIND_LABELS,
  CRM_ACTIVITY_KINDS,
  formatDate,
  type CrmActivityKind,
  type CrmActivityStatus,
} from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { SearchSelect } from './SearchSelect';
import { UserSelect } from './UserSelect';
import { useToast } from './toast';
import { Badge, Checkbox, Dialog, Field, Select, TextArea, TextInput } from './ui';

export type ActivityRow = {
  id: string;
  partnerId: string;
  partnerName: string;
  partnerIsCustomer: boolean;
  contactId: string | null;
  contactName: string | null;
  kind: CrmActivityKind;
  subject: string;
  notes: string | null;
  activityDate: string;
  assignedTo: string | null;
  assignedToName: string | null;
  dueOn: string | null;
  status: CrmActivityStatus;
  completedAt: string | null;
  createdBy: string | null;
};

export function kindBadge(kind: CrmActivityKind) {
  return <Badge kind="neutral">{CRM_ACTIVITY_KIND_LABELS[kind]}</Badge>;
}

/** Faelligkeit mit Warnung bei ueberschrittenem Termin. */
export function dueLabel(row: ActivityRow) {
  if (!row.dueOn) return <span className="faint">ohne Termin</span>;
  const today = new Date().toISOString().slice(0, 10);
  if (row.status === 'open' && row.dueOn < today) {
    return <Badge kind="danger">überfällig · {formatDate(row.dueOn)}</Badge>;
  }
  if (row.status === 'open' && row.dueOn === today) {
    return <Badge kind="warning">heute</Badge>;
  }
  return <>{formatDate(row.dueOn)}</>;
}

/** Alle Abfragen, die Aktivitaeten zeigen, neu laden. */
export function invalidateActivities(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['crm-activities'] });
}

// --- Erfassung ---------------------------------------------------------------

export function CreateActivityDialog({
  partnerId,
  partnerLabel,
  defaultAssignedTo,
  onClose,
}: {
  /** Fester Partner (Kundenakte) - ohne Angabe waehlt der Dialog. */
  partnerId?: string;
  partnerLabel?: string;
  /** Vorbelegter Zustaendiger, z. B. der Aussendienst des Kunden. */
  defaultAssignedTo?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();

  const [chosenPartnerId, setChosenPartnerId] = useState(partnerId ?? '');
  const [contactId, setContactId] = useState('');
  const [kind, setKind] = useState<CrmActivityKind>('call');
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [activityDate, setActivityDate] = useState(new Date().toISOString().slice(0, 10));
  const [followUp, setFollowUp] = useState(false);
  const [dueOn, setDueOn] = useState('');
  const [assignedTo, setAssignedTo] = useState(defaultAssignedTo ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: partnersList } = useQuery({
    queryKey: ['partners', 'all', 'crm'],
    queryFn: () => api.get<ListResponse<Partner>>(withQuery('/api/partners', { limit: 500 })),
    enabled: !partnerId,
  });

  // Ohne festen Partner: nach der Wahl dessen Aussendienst vorbelegen.
  const { data: chosenPartner } = useQuery({
    queryKey: ['partner', chosenPartnerId],
    queryFn: () => api.get<{ partner: Partner }>(`/api/partners/${chosenPartnerId}`),
    enabled: !partnerId && chosenPartnerId !== '',
  });

  // Ansprechpartner des Partners - wer angerufen hat, steht dann dabei.
  const { data: contacts } = useQuery({
    queryKey: ['partner-contacts', chosenPartnerId],
    queryFn: () =>
      api.get<{
        contacts: {
          id: string;
          salutation: string | null;
          firstName: string | null;
          lastName: string;
          position: string | null;
        }[];
      }>(`/api/partners/${chosenPartnerId}/contacts`),
    enabled: chosenPartnerId !== '',
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ activity: { id: string } }>('/api/crm/activities', {
        partnerId: chosenPartnerId,
        contactId: contactId || null,
        kind,
        subject,
        notes: notes || null,
        activityDate,
        dueOn: followUp ? dueOn || null : null,
        assignedTo: assignedTo || null,
      }),
    onSuccess: () => {
      invalidateActivities(queryClient);
      toast.success(followUp ? 'Die Wiedervorlage wurde eingestellt.' : 'Die Aktivität wurde erfasst.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  const fallbackAssignee = chosenPartner?.partner.salesRepId ?? defaultAssignedTo ?? '';

  return (
    <Dialog
      title="Aktivität erfassen"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending || !chosenPartnerId || !subject.trim() || (followUp && !dueOn)}
            onClick={() => create.mutate()}
          >
            Erfassen
          </button>
        </>
      }
    >
      <Field label="Partner" required error={errors.partnerId}>
        {partnerId ? (
          <TextInput value={partnerLabel ?? ''} onChange={() => undefined} disabled />
        ) : (
          <SearchSelect
            value={chosenPartnerId}
            onChange={(value) => {
              setChosenPartnerId(value);
              setContactId('');
              setAssignedTo('');
            }}
            options={(partnersList?.items ?? []).map((partner) => ({
              value: partner.id,
              label: `${partner.number} · ${partner.name}`,
            }))}
            placeholder="Name oder Nummer tippen …"
            invalid={Boolean(errors.partnerId)}
          />
        )}
      </Field>

      {(contacts?.contacts.length ?? 0) > 0 ? (
        <Field label="Ansprechpartner" error={errors.contactId} hint="Mit wem wurde gesprochen?">
          <Select
            value={contactId}
            onChange={setContactId}
            placeholder="— ohne Ansprechpartner —"
            options={(contacts?.contacts ?? []).map((contact) => ({
              value: contact.id,
              label:
                contactDisplayName(contact) + (contact.position ? ` (${contact.position})` : ''),
            }))}
          />
        </Field>
      ) : null}

      <div className="row" style={{ gap: 12 }}>
        <Field label="Art" required error={errors.kind}>
          <Select
            value={kind}
            onChange={(value) => setKind(value)}
            options={CRM_ACTIVITY_KINDS.map((value) => ({
              value,
              label: CRM_ACTIVITY_KIND_LABELS[value],
            }))}
          />
        </Field>
        <Field label="Datum" required error={errors.activityDate}>
          <TextInput type="date" value={activityDate} onChange={setActivityDate} />
        </Field>
      </div>

      <Field label="Betreff" required error={errors.subject}>
        <TextInput
          value={subject}
          onChange={setSubject}
          placeholder="z. B. Rückruf wegen Schachtsanierung"
          invalid={Boolean(errors.subject)}
        />
      </Field>

      <Field label="Notizen" error={errors.notes}>
        <TextArea value={notes} onChange={setNotes} rows={3} />
      </Field>

      <Field
        label="Zuständig"
        error={errors.assignedTo}
        hint={
          followUp
            ? 'Leer = Außendienst des Kunden, sonst Sie selbst'
            : 'Ein Kollege bekommt die Aktivität als Aufgabe mit Wiedervorlage und eine E-Mail.'
        }
      >
        <UserSelect
          value={assignedTo}
          onChange={(value) => {
            setAssignedTo(value);
            // Zuweisung an jemand anderen ist eine Aufgabe: Wiedervorlage
            // heute, Termin laesst sich noch verschieben.
            if (value && value !== user?.id && !followUp) {
              setFollowUp(true);
              if (!dueOn) setDueOn(activityDate);
            }
          }}
          emptyLabel={followUp ? 'Automatisch' : '— niemand —'}
        />
      </Field>

      <Checkbox
        id="crm-follow-up"
        checked={followUp}
        onChange={(value) => {
          setFollowUp(value);
          if (value && !assignedTo) setAssignedTo(fallbackAssignee);
          if (value && !dueOn) setDueOn(activityDate);
        }}
        label="Zur Wiedervorlage"
      />

      {followUp ? (
        <div className="row" style={{ gap: 12, marginTop: 8 }}>
          <Field label="Wiedervorlage am" required error={errors.dueOn}>
            <TextInput type="date" value={dueOn} onChange={setDueOn} />
          </Field>
        </div>
      ) : null}
    </Dialog>
  );
}

// --- Detail und Bearbeitung --------------------------------------------------

export function ActivityDetailDialog({
  activity,
  onClose,
}: {
  activity: ActivityRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const readOnly = !can('crm:write');

  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(activity.subject);
  const [notes, setNotes] = useState(activity.notes ?? '');
  const [dueOn, setDueOn] = useState(activity.dueOn ?? '');
  const [assignedTo, setAssignedTo] = useState(activity.assignedTo ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/crm/activities/${activity.id}`, body),
    onSuccess: () => {
      invalidateActivities(queryClient);
      toast.success('Die Aktivität wurde aktualisiert.');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Die Aktion ist fehlgeschlagen.');
      }
    },
  });

  return (
    <Dialog
      title={`${CRM_ACTIVITY_KIND_LABELS[activity.kind]} · ${formatDate(activity.activityDate)}`}
      onClose={onClose}
      footer={
        !readOnly ? (
          <>
            {activity.status === 'open' ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={patch.isPending}
                onClick={() => patch.mutate({ status: 'done' })}
              >
                Erledigen
              </button>
            ) : activity.dueOn ? (
              <button
                type="button"
                className="btn"
                disabled={patch.isPending}
                onClick={() => patch.mutate({ status: 'open' })}
              >
                Wieder öffnen
              </button>
            ) : null}
            {editing ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={patch.isPending || !subject.trim()}
                onClick={() =>
                  patch.mutate({
                    subject,
                    notes: notes || null,
                    dueOn: dueOn || null,
                    assignedTo: assignedTo || null,
                  })
                }
              >
                Speichern
              </button>
            ) : (
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                Bearbeiten
              </button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: 'center' }}>
        {activity.status === 'open' ? (
          <Badge kind="warning">Offen</Badge>
        ) : (
          <Badge kind="success">Erledigt</Badge>
        )}
        {kindBadge(activity.kind)}
        <span className="faint">{activity.partnerName}</span>
      </div>

      {editing ? (
        <>
          <Field label="Betreff" required error={errors.subject}>
            <TextInput value={subject} onChange={setSubject} invalid={Boolean(errors.subject)} />
          </Field>
          <Field label="Notizen" error={errors.notes}>
            <TextArea value={notes} onChange={setNotes} rows={4} />
          </Field>
          <div className="row" style={{ gap: 12 }}>
            <Field label="Wiedervorlage am" error={errors.dueOn} hint="Leer = keine Wiedervorlage">
              <TextInput type="date" value={dueOn} onChange={setDueOn} />
            </Field>
            <Field label="Zuständig" error={errors.assignedTo}>
              <UserSelect value={assignedTo} onChange={setAssignedTo} />
            </Field>
          </div>
        </>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <tbody>
              <tr>
                <td className="faint" style={{ width: '11em' }}>Betreff</td>
                <td>{activity.subject}</td>
              </tr>
              {activity.notes ? (
                <tr>
                  <td className="faint">Notizen</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{activity.notes}</td>
                </tr>
              ) : null}
              <tr>
                <td className="faint">Wiedervorlage</td>
                <td>{dueLabel(activity)}</td>
              </tr>
              {activity.contactName ? (
                <tr>
                  <td className="faint">Ansprechpartner</td>
                  <td>{activity.contactName}</td>
                </tr>
              ) : null}
              <tr>
                <td className="faint">Zuständig</td>
                <td>{activity.assignedToName ?? <span className="faint">—</span>}</td>
              </tr>
              {activity.completedAt ? (
                <tr>
                  <td className="faint">Erledigt am</td>
                  <td>{new Date(activity.completedAt).toLocaleString('de-DE')}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
